// Shared "additional fields" block for Project create/edit forms.
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";

/**
 * Searchable freelancer-name autocomplete.
 * - Suggestions only appear as the user types (no full list by default).
 * - Each instance owns its own search state and is independent of siblings.
 */
function FreelancerAutocomplete({ value, onChange, freelancers = [], disabled, testid, placeholder }) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const q = (value || "").trim().toLowerCase();
  const matches = (focused && q.length > 0)
    ? freelancers.filter((f) => (f.name || "").toLowerCase().includes(q)).slice(0, 8)
    : [];
  return (
    <div className="relative" ref={rootRef}>
      <Input
        data-testid={testid}
        value={value || ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        className="rounded-lg border-black/15"
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-black/15 rounded-lg shadow-md max-h-48 overflow-auto" data-testid={`${testid}-suggestions`}>
          {matches.map((f) => (
            <button
              type="button"
              key={f.id}
              data-testid={`${testid}-opt-${f.id}`}
              onClick={() => { onChange(f.name); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-cream-2 flex items-center justify-between gap-2"
            >
              <span>{f.name}</span>
              {f.category && <span className="text-xs text-neutral-500">{f.category}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectExtraFields({ value, onChange, idPrefix = "edit-project", disabled = false, freelancers = [], serviceLocked = false }) {
  const v = {
    service_name: "", writer: "", reviewer: "", biostatistician: "", uploader: "",
    journals: [{ name: "", url: "" }, { name: "", url: "" }, { name: "", url: "" }],
    ...(value || {}),
  };
  // Normalise journals to always be a length-3 array
  const journals = Array.from({ length: 3 }).map((_, i) => v.journals?.[i] || { name: "", url: "" });
  const setJ = (i, patch) => {
    const next = journals.map((j, idx) => (idx === i ? { ...j, ...patch } : j));
    onChange({ ...v, journals: next });
  };
  return (
    <>
      <div><label className="label-caps block mb-1">Service Name</label>
        <Input data-testid={`${idPrefix}-service-name`} value={v.service_name || ""}
          disabled={disabled || serviceLocked}
          title={serviceLocked ? "Only Super Admin, Operation Head or Team Leader can change the Service" : ""}
          onChange={(e) => onChange({ ...v, service_name: e.target.value })}
          className="rounded-lg border-black/15" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="label-caps block mb-1">Writer</label>
          <FreelancerAutocomplete value={v.writer} onChange={(x) => onChange({ ...v, writer: x })}
            freelancers={freelancers} disabled={disabled} testid={`${idPrefix}-writer`} placeholder="Start typing a name…" /></div>
        <div><label className="label-caps block mb-1">Reviewer</label>
          <FreelancerAutocomplete value={v.reviewer} onChange={(x) => onChange({ ...v, reviewer: x })}
            freelancers={freelancers} disabled={disabled} testid={`${idPrefix}-reviewer`} placeholder="Start typing a name…" /></div>
        <div><label className="label-caps block mb-1">Biostatistician</label>
          <FreelancerAutocomplete value={v.biostatistician} onChange={(x) => onChange({ ...v, biostatistician: x })}
            freelancers={freelancers} disabled={disabled} testid={`${idPrefix}-biostat`} placeholder="Start typing a name…" /></div>
        <div><label className="label-caps block mb-1">Uploader</label>
          <FreelancerAutocomplete value={v.uploader} onChange={(x) => onChange({ ...v, uploader: x })}
            freelancers={freelancers} disabled={disabled} testid={`${idPrefix}-uploader`} placeholder="Start typing a name…" /></div>
      </div>
      <div><label className="label-caps block mb-1">Journals</label>
        <div className="space-y-2">
          {journals.map((j, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input data-testid={`${idPrefix}-journal-name-${i}`} placeholder={`Journal ${i + 1} name`} value={j.name || ""}
                disabled={disabled}
                onChange={(e) => setJ(i, { name: e.target.value })}
                className="rounded-lg border-black/15" />
              <Input data-testid={`${idPrefix}-journal-url-${i}`} type="url" placeholder="https://…" value={j.url || ""}
                disabled={disabled}
                onChange={(e) => setJ(i, { url: e.target.value })}
                className="rounded-lg border-black/15" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export function formatNoteTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase();
    return `${date}, ${time}`;
  } catch { return iso; }
}
